export type UserDayRecord = {
  id: string;
  name: string;
  initials: string;
  team: string;
  color: string;
  isPerfect: boolean;
  habitsComplete: number;
  habitsTotal: number;
  steps: number;
  targetSteps: number;
  sleepHours: number;
  streak: number;
  note: string;
};

export type DaySummary = {
  dateKey: string;
  perfectCount: number;
  totalUsers: number;
  records: UserDayRecord[];
};

export type UserProfile = Omit<UserDayRecord, "isPerfect" | "habitsComplete" | "habitsTotal" | "steps" | "targetSteps" | "sleepHours" | "streak" | "note">;

const profiles: UserProfile[] = [
  { id: "maya", name: "Maya Chen", initials: "MC", team: "Product", color: "bg-violet-500" },
  { id: "jordan", name: "Jordan Bell", initials: "JB", team: "Engineering", color: "bg-sky-500" },
  { id: "devon", name: "Devon Ross", initials: "DR", team: "Design", color: "bg-rose-500" },
  { id: "samira", name: "Samira Khan", initials: "SK", team: "Marketing", color: "bg-amber-500" },
  { id: "leo", name: "Leo Martin", initials: "LM", team: "Sales", color: "bg-emerald-500" },
  { id: "nia", name: "Nia Wright", initials: "NW", team: "Operations", color: "bg-fuchsia-500" },
  { id: "owen", name: "Owen Park", initials: "OP", team: "Engineering", color: "bg-cyan-500" },
  { id: "elena", name: "Elena Ortiz", initials: "EO", team: "Product", color: "bg-orange-500" },
  { id: "kai", name: "Kai Thompson", initials: "KT", team: "Support", color: "bg-indigo-500" },
  { id: "iris", name: "Iris Ali", initials: "IA", team: "People", color: "bg-pink-500" },
  { id: "noah", name: "Noah Brooks", initials: "NB", team: "Finance", color: "bg-lime-600" },
  { id: "zoe", name: "Zoë Reed", initials: "ZR", team: "Design", color: "bg-teal-500" },
];

const notes = [
  "All daily goals complete.",
  "Steps goal missed by a small margin.",
  "One habit left to check in.",
  "Strong consistency—keep it going.",
  "Sleep target was the only miss.",
  "Back on track after a busy week.",
];

export function getDateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function getUsers(): UserProfile[] {
  return profiles;
}

export function filterDaySummaryByUser(summary: DaySummary, userId: string | null): DaySummary {
  if (!userId) {
    return summary;
  }

  const records = summary.records.filter((record) => record.id === userId);
  return {
    ...summary,
    perfectCount: records.filter((record) => record.isPerfect).length,
    totalUsers: records.length,
    records,
  };
}

export function getDaySummary(date: Date): DaySummary {
  const daySeed = date.getFullYear() * 17 + (date.getMonth() + 1) * 11 + date.getDate() * 7;
  const records = profiles.map((profile, index) => {
    const value = (daySeed + index * 13) % 10;
    const isPerfect = value >= 3;
    const habitsTotal = 4;
    const habitsComplete = isPerfect ? habitsTotal : Math.max(2, habitsTotal - ((value % 2) + 1));
    const targetSteps = 8000;
    const steps = isPerfect ? 8200 + ((daySeed + index * 191) % 3400) : 5700 + ((daySeed + index * 313) % 2400);

    return {
      ...profile,
      isPerfect,
      habitsComplete,
      habitsTotal,
      steps,
      targetSteps,
      sleepHours: Number((isPerfect ? 7.1 + ((index + daySeed) % 17) / 10 : 5.8 + ((index + daySeed) % 12) / 10).toFixed(1)),
      streak: 2 + ((daySeed + index * 5) % 24),
      note: isPerfect ? notes[index % 2 === 0 ? 0 : 3] : notes[1 + (index % 4)],
    };
  });

  return {
    dateKey: getDateKey(date),
    perfectCount: records.filter((record) => record.isPerfect).length,
    totalUsers: records.length,
    records,
  };
}
